import React, { useEffect, useRef, useState } from "react";
import { MonthKey } from "../types";
import { MonthTotals } from "../selectors";
import { formatMoney, formatMonth } from "../format";

interface MonthlyTrendProps {
    data: MonthTotals[];
    selected: MonthKey;
    currency: string;
    onSelect: (month: MonthKey) => void;
}

// Drawn at the container's real width (not scaled), so labels stay 11px on phones
const DEFAULT_W = 560;
const H = 220;
const PAD = { top: 12, right: 8, bottom: 28, left: 52 };
const BAR_GAP = 2;

// Rectangle with only the top corners rounded, so the bar stays anchored to the baseline
function barPath(x: number, y: number, w: number, h: number, r = 4): string {
    const radius = Math.min(r, w / 2, h);
    return `M${x},${y + h} V${y + radius} Q${x},${y} ${x + radius},${y} H${x + w - radius} Q${x + w},${y} ${x + w},${y + radius} V${y + h} Z`;
}

function niceMax(value: number): number {
    if (value <= 0) {
        return 100_00;
    }
    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    const steps = [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 10];
    const step = steps.find(s => s * magnitude >= value) || 10;
    return step * magnitude;
}

export function MonthlyTrend({ data, selected, currency, onSelect }: MonthlyTrendProps) {
    const [hover, setHover] = useState<number | null>(null);
    const chartRef = useRef<HTMLDivElement>(null);
    const [W, setW] = useState(DEFAULT_W);

    useEffect(() => {
        const el = chartRef.current;
        if (!el || typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(entries => setW(Math.max(280, Math.round(entries[0].contentRect.width))));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const max = niceMax(Math.max(...data.map(d => Math.max(d.income, d.expenses))));
    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;
    const slot = plotW / data.length;
    const barW = Math.min(26, (slot * 0.62 - BAR_GAP) / 2);
    const y = (v: number) => PAD.top + plotH - (v / max) * plotH;
    const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => t * max);
    const hovered = hover !== null ? data[hover] : null;

    return (
        <section className="fin-card">
            <div className="fin-card-head">
                <h3>Income vs expenses</h3>
                <div className="fin-legend" aria-hidden="true">
                    <span><i className="fin-swatch income" /> Income</span>
                    <span><i className="fin-swatch expense" /> Expenses</span>
                </div>
            </div>
            <div className="fin-chart" ref={chartRef}>
                <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Monthly income and expenses for the last six months">
                    {ticks.map(t => (
                        <g key={t}>
                            <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} className="fin-gridline" />
                            <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="fin-axis">
                                {formatMoney(t, currency, true)}
                            </text>
                        </g>
                    ))}
                    {data.map((d, i) => {
                        const cx = PAD.left + slot * i + slot / 2;
                        const isSelected = d.month === selected;
                        return (
                            <g key={d.month}>
                                {isSelected && (
                                    <rect x={PAD.left + slot * i + 2} y={PAD.top} width={slot - 4} height={plotH} rx={6} className="fin-selected" />
                                )}
                                <path d={barPath(cx - barW - BAR_GAP / 2, y(d.income), barW, PAD.top + plotH - y(d.income))} className="fin-bar income" />
                                <path d={barPath(cx + BAR_GAP / 2, y(d.expenses), barW, PAD.top + plotH - y(d.expenses))} className="fin-bar expense" />
                                <text x={cx} y={H - 8} textAnchor="middle" className={`fin-axis ${isSelected ? "selected" : ""}`}>
                                    {formatMonth(d.month, "short")}
                                </text>
                                {/* Hit target covers the whole month column, larger than the bars */}
                                <rect
                                    x={PAD.left + slot * i}
                                    y={PAD.top}
                                    width={slot}
                                    height={plotH + PAD.bottom}
                                    className="fin-hit"
                                    onMouseEnter={() => setHover(i)}
                                    onMouseLeave={() => setHover(null)}
                                    onClick={() => onSelect(d.month)}
                                />
                            </g>
                        );
                    })}
                </svg>
                {hovered && hover !== null && (
                    <div className="fin-tooltip" style={{ left: `${((PAD.left + slot * hover + slot / 2) / W) * 100}%` }}>
                        <strong>{formatMonth(hovered.month)}</strong>
                        <span><i className="fin-swatch income" /> Income {formatMoney(hovered.income, currency)}</span>
                        <span><i className="fin-swatch expense" /> Expenses {formatMoney(hovered.expenses, currency)}</span>
                        <span className="fin-tooltip-net">Net {formatMoney(hovered.income - hovered.expenses, currency)}</span>
                        <small>Click to view this month</small>
                    </div>
                )}
            </div>
        </section>
    );
}
